import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmByTypingTextDialogProps } from ".";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";

export function ConfirmByTypingTextDialog({
    title = "Are you sure?",
    closeButton,
    open: _open,
    onOpenChange: _onOpenChange,
    defaultOpen,
    dialogTrigger,
    confirmButton,
    confirmText = "DELETE",
    onSubmit: _onSubmit,
    ...props
}: ConfirmByTypingTextDialogProps) {
    const [controlledOpen, setControlledOpen] = useState(false)
    const open = _open ?? controlledOpen
    const onOpenChange = _onOpenChange ?? setControlledOpen
    const schema = z.object({
        confirmText: z.literal(confirmText, {
            errorMap: () => ({ message: `You must type "${confirmText}" to confirm` })
        })
    })
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            confirmText: ""
        }
    })
    useEffect(() => {
        if (open) {
            form.reset()
        }
    }, [open])
    const onSubmit = () => {
        _onSubmit()
        onOpenChange(false)
    }
    return (
        <Dialog open={open} onOpenChange={onOpenChange} {...props}>
            {
                dialogTrigger ? <DialogTrigger asChild>{dialogTrigger}</DialogTrigger> : null
            }
            <DialogContent>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <DialogHeader>
                            <DialogTitle>
                                {title}
                            </DialogTitle>
                            <DialogDescription>
                                Please type <strong>{confirmText}</strong> to confirm.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="">
                            <FormField
                                control={form.control}
                                name="confirmText"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <Input placeholder={confirmText} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <DialogFooter>
                            <DialogClose asChild>
                                {closeButton ?? <Button variant="outline" size='sm'>Cancel</Button>}
                            </DialogClose>
                            {confirmButton ?? <Button variant='destructive' type='submit' size='sm'>Confirm</Button>}
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog >
    )
}